import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hairline, Icon, RubberStamp, Swatch, Tape } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { WalkthroughOverlay } from "@/components/WalkthroughOverlay";
import { useTopCollectors } from "@/hooks/useTopCollectors";
import { useUnseenGrailMatches } from "@/hooks/useFigData";
import { useWeeklyFeature, getPrevPtMondayISO, type FotwCandidateRevealed } from "@/hooks/useWeeklyFeature";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";


// ─────────────────────────────────────────────────────────────────────────────
// Home is the EDITORIAL / DISCOVERY surface. It is intentionally *not* a feed.
// It surfaces algorithm-curated picks: Featured Find of the Day, Trending
// Brands this week, Top Collectors, and a Featured Grails strip.
// The chronological social feed (Following / Nearby / Global) lives on /feed.
// ─────────────────────────────────────────────────────────────────────────────

type FeaturedScan = {
  id: string;
  title: string;
  brand: string | null;
  era: string | null;
  price_suggested: number | null;
  confidence: number | null;
  verdict: string;
  img: string;
  photos: string[];
  user_id: string;
  caption: string | null;
  handle: string;
  display_name: string | null;
  avatar: string;
  created_at: string;
  status: string | null;
  marketplace_links: { name: string; url: string }[] | null;
};

type TrendingBrand = {
  brand: string;
  count: number;
  avgPrice: number | null;
};

export default function Home() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [bannerHidden, setBannerHidden] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [featured, setFeatured] = useState<FeaturedScan | null>(null);
  const [recentPicks, setRecentPicks] = useState<FeaturedScan[]>([]);
  const [trendingBrands, setTrendingBrands] = useState<TrendingBrand[]>([]);
  const [loading, setLoading] = useState(true);
  // Personalised match callout — only shown when the signed-in user actually
  // has unseen matches. Hook is auth-aware and returns 0 for guests.
  const { count: grailUnseen } = useUnseenGrailMatches();
  // Find of the Week — current open week for the CTA, previous finalized
  // week for the persistent winner card on Home.
  const fotwCurrent = useWeeklyFeature();
  const fotwPrev = useWeeklyFeature(getPrevPtMondayISO());

  // Welcome banner — shown once per user after onboarding, dismissible.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("onboarding_banner_dismissed")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      const dismissed = (data as { onboarding_banner_dismissed?: boolean } | null)?.onboarding_banner_dismissed;
      if (!dismissed) setShowBanner(true);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const dismissBanner = async () => {
    setBannerHidden(true);
    if (user) {
      await supabase
        .from("profiles")
        .update({ onboarding_banner_dismissed: true } as never)
        .eq("id", user.id);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1. Pull recent posted scans (we'll pick featured + secondary picks from these).
      //    Widen the window to 7 days so the ranking has a real pool to draw
      //    from, not just the last few hours of activity.
      const sevenDaysAgo = new Date(Date.now() - 7 * 86_400_000).toISOString();
      const { data: scans } = await supabase
        .from("scans")
        .select("id,title,brand,era,price_suggested,confidence,verdict,img,photos,user_id,caption,created_at,status,marketplace_links")
        .eq("posted", true)
        .eq("verdict", "PASS")
        .is("suspended_at", null)
        // Only items that are currently listed (not kept, sold, or de-listed).
        // Legacy rows with no status set are included so old posts aren't excluded.
        .or("status.eq.listed,status.is.null")
        .not("marketplace_links", "is", null)
        .gte("created_at", sevenDaysAgo)
        .order("created_at", { ascending: false })
        .limit(40);

      // 2. Pull author profiles for those scans.
      const userIds = Array.from(new Set((scans ?? []).map((s) => s.user_id)));
      const { data: profiles } = userIds.length
        ? await (supabase as any)
            .from("profiles_public")
            .select("id,handle,display_name,avatar")
            .in("id", userIds)
        : { data: [] as { id: string; handle: string; display_name: string | null; avatar: string }[] };

      const profById = new Map(
        ((profiles ?? []) as Array<{ id: string; handle: string; display_name: string | null; avatar: string }>)
          .map((p) => [p.id, p] as const)
      );

      const enriched: FeaturedScan[] = (scans ?? []).map((s) => {
        const p = profById.get(s.user_id);
        const links = Array.isArray(s.marketplace_links) ? (s.marketplace_links as { name: string; url: string }[]) : null;
        return {
          id: s.id,
          title: s.title,
          brand: s.brand,
          era: s.era,
          price_suggested: s.price_suggested,
          confidence: s.confidence,
          verdict: s.verdict,
          img: s.img,
          photos: s.photos ?? [],
          user_id: s.user_id,
          caption: s.caption,
          handle: p?.handle ?? "anon",
          display_name: p?.display_name ?? null,
          avatar: p?.avatar ?? "olive",
          created_at: s.created_at,
          status: (s as Record<string, unknown>).status as string | null ?? null,
          marketplace_links: links?.length ? links : null,
        };
      });

      // ── Find of the Day ranking ──────────────────────────────────────
      // Ranked by AI confidence. Rotation: skip users who won within the
      // last 1 day so the same power user can't headline two days in a row.
      const scoreOf = (s: FeaturedScan) => s.confidence ?? 0;

      const COOLDOWN_KEY = "fig:fotd-recent-winners";
      const oneDayAgo = Date.now() - 86_400_000;
      type Winner = { user_id: string; at: number };
      let recentWinners: Winner[] = [];
      try {
        const raw = localStorage.getItem(COOLDOWN_KEY);
        if (raw) recentWinners = (JSON.parse(raw) as Winner[]).filter((w) => w.at > oneDayAgo);
      } catch { /* ignore */ }
      const onCooldown = new Set(recentWinners.map((w) => w.user_id));

      const ranked = [...enriched].sort((a, b) => scoreOf(b) - scoreOf(a));
      const headliner = ranked.find((s) => !onCooldown.has(s.user_id)) ?? ranked[0] ?? null;

      if (headliner) {
        const next: Winner[] = [
          { user_id: headliner.user_id, at: Date.now() },
          ...recentWinners.filter((w) => w.user_id !== headliner.user_id),
        ].slice(0, 10);
        try { localStorage.setItem(COOLDOWN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      }

      const others = enriched.filter((s) => s.id !== headliner?.id).slice(0, 4);

      // 3. Trending brands across all posted scans.
      const { data: allPosted, error: brandsErr } = await supabase
        .from("scans")
        .select("brand,price_suggested")
        .eq("posted", true)
        .is("suspended_at", null)
        .not("brand", "is", null);
      console.log("[Home] brands query →", { rows: allPosted?.length ?? 0, error: brandsErr });

      const brandMap = new Map<string, { count: number; sum: number; n: number }>();
      (allPosted ?? []).forEach((s) => {
        const b = (s.brand ?? "").trim();
        if (!b || b.toLowerCase() === "unknown") return;
        const cur = brandMap.get(b) ?? { count: 0, sum: 0, n: 0 };
        cur.count += 1;
        if (s.price_suggested != null) {
          cur.sum += Number(s.price_suggested);
          cur.n += 1;
        }
        brandMap.set(b, cur);
      });
      const trending: TrendingBrand[] = Array.from(brandMap.entries())
        .map(([brand, v]) => ({
          brand,
          count: v.count,
          avgPrice: v.n > 0 ? Math.round(v.sum / v.n) : null,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      if (cancelled) return;
      setFeatured(headliner);
      setRecentPicks(others);
      setTrendingBrands(trending);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalFiledToday = useMemo(() => {
    // Cute editorial "items filed" line — pulled from what we loaded.
    return recentPicks.length + (featured ? 1 : 0);
  }, [recentPicks, featured]);

  // Live masthead date + issue number. Issue № increments daily from a fixed
  // launch epoch so it feels like a real periodical that keeps shipping.
  const { mastheadDate, issueNumber } = useMemo(() => {
    const now = new Date();
    const dateStr = now.toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    }).replace(/,/g, " ·").replace(/\s+·\s+/g, " · ");
    // Launch epoch: Apr 18 2026 = Issue № 241 (matches the original hard-coded value).
    const epoch = new Date(2026, 3, 18).getTime();
    const days = Math.floor((now.getTime() - epoch) / 86_400_000);
    return { mastheadDate: dateStr, issueNumber: 241 + Math.max(0, days) };
  }, []);

  const featuredImg =
    featured && (featured.photos[0] || featured.img);
  const featuredIsUrl =
    typeof featuredImg === "string" && /^https?:\/\//.test(featuredImg);

  return (
    <MobileFrame>
      {/* First-time spotlight tour. Self-gates on profiles.onboarding_complete
          AND !profiles.walkthrough_complete, so it only paints on the user's
          first visit to /home after onboarding finishes. Marks
          walkthrough_complete on close so it never re-shows. */}
      <WalkthroughOverlay />
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 80 }}>
        {/* ─── Masthead ────────────────────────────────────────────────── */}
        <div style={{ padding: "12px 20px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <span className="caps">{mastheadDate}</span>
          </div>
          <h1 style={{
            fontFamily: "'Fraunces', 'Fraunces', 'Playfair Display', serif", fontWeight: 900,
            fontStyle: "italic", fontSize: 84,
            lineHeight: 0.85, color: "hsl(var(--ink))", letterSpacing: "-0.06em",
            margin: "0 0 10px",
            fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
          }}>fig</h1>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
            <span style={{ fontFamily: "'Fraunces', 'Fraunces', 'Playfair Display', serif", fontStyle: "italic", fontSize: 15, color: "hsl(var(--rust))" }}>
              Fashion · Intentionally · Gathered
            </span>
          </div>
        </div>

        <div style={{ padding: "0 20px" }}>
          <Hairline style={{ margin: "14px 0" }} />
        </div>

        {showBanner && !bannerHidden && (
          <div style={{ padding: "0 20px", marginBottom: 14 }}>
            <div style={{
              border: "1.5px solid hsl(var(--ink))",
              background: "hsl(var(--cream))",
              padding: "12px 14px",
              display: "flex", alignItems: "flex-start", gap: 12,
              position: "relative",
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontFamily: "'Cormorant Garamond', 'Fraunces', 'Playfair Display', serif",
                  fontStyle: "italic", fontWeight: 600, fontSize: 22,
                  color: "hsl(var(--ink))", lineHeight: 1.05,
                }}>
                  Welcome to FIG.
                </div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14,
                  color: "hsl(var(--ink-2))", marginTop: 4, lineHeight: 1.35,
                }}>
                  Your closet is waiting — post your first find.
                </div>
              </div>
              <button
                onClick={dismissBanner}
                aria-label="Dismiss welcome"
                style={{
                  background: "none", border: "1px solid hsl(var(--ink))",
                  width: 28, height: 28, cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 14,
                  color: "hsl(var(--ink))", flexShrink: 0,
                }}
              >×</button>
            </div>
          </div>
        )}

        {/* ─── GRAIL MATCH CALLOUT (signed-in users with unseen hits) ────
         * Reordered above the Scan CTA so urgent personal alerts read first
         * — the eye should land on "your grails just hit" before "scan
         * something". Hidden when there's nothing to flag. */}
        {grailUnseen > 0 && (
          <div style={{ padding: "0 20px", marginBottom: 14 }}>
            <button
              onClick={() => nav("/grails?focus=matches")}
              style={{
                width: "100%",
                textAlign: "left",
                border: "1.5px solid hsl(var(--ink))",
                background: "hsl(var(--marigold))",
                color: "hsl(var(--marigold-fg))",
                padding: "14px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 14,
                position: "relative",
              }}
            >
              <div
                style={{
                  width: 42, height: 42, borderRadius: "50%",
                  border: "1.5px solid hsl(var(--ink))",
                  background: "hsl(var(--ink))",
                  color: "hsl(var(--marigold))",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontWeight: 900, fontSize: 18, flexShrink: 0,
                }}
              >
                {grailUnseen > 9 ? "9+" : grailUnseen}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontWeight: 800, fontSize: 18, lineHeight: 1.1,
                }}>
                  {grailUnseen === 1 ? "A grail just hit" : "Grails just hit your inbox"}
                </div>
                <div className="caps" style={{ marginTop: 3, color: "hsl(var(--ink-2))" }}>
                  {grailUnseen === 1
                    ? "Someone posted a piece from your want list"
                    : `${grailUnseen} new pieces from your want list`}
                </div>
              </div>
              {Icon.chev(16)}
            </button>
          </div>
        )}

        {/* ─── FIND OF THE WEEK — voting CTA + last-week winner ──────────── */}
        {(() => {
          const cur = fotwCurrent.data;
          const hasOpenWeek =
            cur && !cur.finalized && !cur.skipped && (cur.candidates?.length ?? 0) > 0;
          if (!hasOpenWeek) return null;
          const dow = new Date().getDay();
          const subline =
            dow >= 1 && dow <= 3 ? "Vote in this week's Find" :
            dow >= 4 && dow <= 6 ? "Voting closes Sunday" :
            "Last day to vote";
          return (
            <div style={{ padding: "0 20px 14px" }}>
              <button
                onClick={() => nav("/find-of-the-week")}
                style={{
                  width: "100%", textAlign: "left",
                  background: "hsl(var(--cream))",
                  border: "1.5px solid hsl(var(--ink))",
                  borderRadius: 14,
                  padding: "12px 16px",
                  cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 12,
                }}
              >
                <span style={{ fontSize: 22, lineHeight: 1 }}>🗳️</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: 800, fontSize: 16, color: "hsl(var(--ink))",
                    lineHeight: 1.1,
                  }}>
                    Find of the Week
                  </div>
                  <div className="caps" style={{ color: "hsl(var(--rust))", marginTop: 4 }}>
                    {subline}
                  </div>
                </div>
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 16, color: "hsl(var(--ink))",
                }}>
                  →
                </span>
              </button>
            </div>
          );
        })()}

        {(() => {
          const prev = fotwPrev.data;
          if (!prev || !prev.finalized || !prev.winning_scan_id) return null;
          // Find the winner row in the revealed payload.
          const winner = (prev.candidates ?? []).find(
            (c): c is FotwCandidateRevealed =>
              "is_winner" in c && (c as FotwCandidateRevealed).is_winner,
          );
          if (!winner) return null;
          const photo = winner.photos?.[0];
          const weekLabel = (() => {
            try {
              return new Date(prev.week_start + "T00:00:00").toLocaleDateString("en-US", {
                month: "short", day: "numeric",
              });
            } catch { return prev.week_start; }
          })();
          return (
            <div style={{ padding: "0 20px 22px" }}>
              <button
                onClick={() => nav(`/post/${winner.scan_id}`)}
                style={{
                  width: "100%", padding: 0, textAlign: "left",
                  background: "hsl(var(--cream))",
                  border: "1.5px solid hsl(var(--ink))",
                  borderRadius: 14,
                  cursor: "pointer",
                  display: "flex", alignItems: "stretch", gap: 0,
                  overflow: "hidden",
                }}
              >
                <div style={{
                  width: 96, flexShrink: 0, position: "relative",
                  background: "hsl(var(--paper-3))",
                }}>
                  {photo && (
                    <img
                      src={photo}
                      alt={winner.brand || "Winner"}
                      style={{
                        position: "absolute", inset: 0,
                        width: "100%", height: "100%",
                        objectFit: "cover", display: "block",
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1, padding: "10px 12px", minWidth: 0 }}>
                  <div className="caps" style={{ color: "hsl(var(--rust))" }}>
                    🏆 Find of the Week · Week of {weekLabel}
                  </div>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: 800, fontSize: 16, color: "hsl(var(--ink))",
                    marginTop: 4, lineHeight: 1.15,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>
                    @{winner.handle}{winner.brand ? ` · ${winner.brand}` : ""}
                  </div>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 10, letterSpacing: "0.1em",
                    color: "hsl(var(--ink-2))", marginTop: 4,
                  }}>
                    View the winning post →
                  </div>
                </div>
              </button>
            </div>
          );
        })()}

        {/* ─── FEATURED FIND OF THE DAY ────────────────────────────────── */}
        <div style={{ padding: "0 20px 6px", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 24, color: "hsl(var(--ink))", letterSpacing: "-0.02em" }}>
            Find of the Day
          </div>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>Editor's pick</span>
        </div>

        <div style={{ padding: "0 20px", marginBottom: 22 }}>
          {loading && (
            <div style={{ height: 280, border: "1px dashed hsl(var(--ink-2))", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
              loading the desk…
            </div>
          )}
          {!loading && !featured && (
            <div style={{ padding: "20px", border: "1px dashed hsl(var(--ink-2))", textAlign: "center" }}>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
                No published finds yet. Scan something — yours could lead the issue.
              </div>
            </div>
          )}
          {!loading && featured && (
            <button
              onClick={() => nav(`/post/${featured.id}`)}
              style={{ padding: 0, border: "1.5px solid hsl(var(--ink))", background: "hsl(var(--cream))", width: "100%", cursor: "pointer", textAlign: "left", display: "block" }}
            >
              {featuredIsUrl ? (
                <div style={{ position: "relative", width: "100%", overflow: "hidden" }}>
                  <img
                    src={featuredImg as string}
                    alt={featured.title}
                    style={{
                      // Natural aspect — Find of the Day shows the photo
                      // at whatever size it is, no letterboxing, no crop.
                      width: "100%",
                      height: "auto",
                      display: "block",
                    }}
                  />
                  <div style={{ position: "absolute", top: 12, right: 12 }}>
                    <RubberStamp
                      label=""
                      sub=""
                      color="hsl(var(--rust))"
                      size={78}
                    />
                  </div>
                </div>
              ) : (
                <Swatch kind={(featured.img as never) || "olive"} style={{ width: "100%", aspectRatio: "4/3", position: "relative" }}>
                  <div style={{ position: "absolute", top: 12, right: 12 }}>
                    <RubberStamp label="" sub="" color="hsl(var(--rust))" size={78} />
                  </div>
                </Swatch>
              )}
              <div style={{ padding: "12px 14px 14px" }}>
                <div className="caps" style={{ color: "hsl(var(--rust))" }}>{featured.era ?? "Vintage"} · {featured.brand ?? "Unbranded"}</div>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 22, color: "hsl(var(--ink))", lineHeight: 1.1, marginTop: 4 }}>
                  {featured.title}
                </div>
                {featured.caption && (
                  <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, color: "hsl(var(--ink-2))", marginTop: 8, lineHeight: 1.35 }}>
                    "{featured.caption}"
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                  <Swatch kind={(featured.avatar as never) || "olive"} style={{ width: 22, height: 22, borderRadius: "50%", border: "1px solid hsl(var(--ink))" }} />
                  <span className="caps">filed by @{featured.handle}</span>
                </div>
              </div>
            </button>
          )}
        </div>

        {/* ─── Scan CTAs ────────────────────────────────────────────────── */}
        {/* Quick Thrift first (free, fast, for in-store buy decisions),
            then Full Verify (the rigorous comp-anchored authentication). */}
        <div style={{ padding: "0 20px", marginBottom: 22 }}>
          <button onClick={() => nav("/scan/quick")} style={{
            width: "100%", textAlign: "left", border: "1.5px solid hsl(var(--ink))",
            background: "hsl(var(--cream))", color: "hsl(var(--ink))", padding: "14px 18px",
            marginBottom: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: 14,
          }}>
            <div style={{
              width: 38, height: 38, border: "1.5px solid hsl(var(--ink))",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {Icon.camera(20)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontWeight: 700, fontSize: 16, lineHeight: 1.1,
              }}>
                Quick Thrift
              </div>
              <div className="caps" style={{
                color: "hsl(var(--rust))", opacity: 0.85, marginTop: 3, fontSize: 10,
              }}>
                1 photo · ~3 sec · free
              </div>
            </div>
            {Icon.chev(14)}
          </button>

          <div style={{ position: "relative" }}>
            <Tape top={-10} left="50%" rotate={-3} width={72} />
            <button onClick={() => nav("/scan")} style={{
              width: "100%", textAlign: "left", border: "1.5px solid hsl(var(--ink))",
              background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))", padding: "20px 22px",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 16,
            }}>
              <div style={{ width: 54, height: 54, border: "1.5px solid hsl(var(--cream))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {Icon.camera(28)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 22, lineHeight: 1 }}>
                  Full Verify
                </div>
                <div className="caps" style={{ color: "hsl(var(--ink-fg))", opacity: 0.75, marginTop: 4 }}>
                  Full authentication · comp pricing
                </div>
              </div>
              {Icon.chev(16)}
            </button>
          </div>
        </div>

        {/* ─── TOP COLLECTORS (real data, last 7 days) ─────────────────────
         * Moved above Trending Brands so the page reads people-first:
         * Find of the Day → who's collecting → what they're collecting
         * (brands) → what they're hunting (grails). */}
        <TopCollectorsStrip />

        {/* ─── TRENDING BRANDS ─────────────────────────────────────────── */}
        {trendingBrands.length > 0 && (
          <>
            <div style={{ padding: "0 20px 6px", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 22, color: "hsl(var(--ink))", letterSpacing: "-0.02em" }}>
                Trending Brands
              </div>
              <span className="caps">this week</span>
            </div>
            <div
              className="no-scrollbar"
              style={{
                display: "flex", gap: 10, overflowX: "auto",
                // Symmetric 20px gutters + scroll-padding so the
                // scroll-snap stops sit INSIDE the horizontal gutters
                // instead of letting the first/last card kiss the
                // viewport edge. Without scroll-padding, cards at
                // either end appeared to clip past the screen edge.
                padding: "0 20px 22px",
                scrollPaddingInlineStart: "20px",
                scrollPaddingInlineEnd: "20px",
                scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch",
              }}
            >
              {trendingBrands.map((b, i) => (
                <button
                  key={b.brand}
                  onClick={() => nav(`/brand/${encodeURIComponent(b.brand)}`)}
                  style={{
                    // 140px wide so two full cards + the right gutter fit on
                    // a 390px iPhone viewport (2 * 140 + 10 gap = 290; plus
                    // 40px combined gutters = 330; leaves room for a peek of
                    // the third card as a scroll affordance without clipping
                    // the second card past the screen edge).
                    flex: "0 0 140px", scrollSnapAlign: "start",
                    border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))",
                    padding: "12px 12px 14px", textAlign: "left", cursor: "pointer",
                    display: "flex", flexDirection: "column", gap: 6, position: "relative",
                  }}
                >
                  <span style={{
                    position: "absolute", top: 8, right: 10,
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 22,
                    color: i === 0 ? "hsl(var(--rust))" : "hsl(var(--ink-2))", lineHeight: 1,
                  }}>{i + 1}</span>
                  <span className="caps" style={{ color: "hsl(var(--rust))" }}>Brand</span>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 17,
                    color: "hsl(var(--ink))", lineHeight: 1.15, paddingRight: 22,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {b.brand}
                  </div>
                  <div className="caps" style={{ color: "hsl(var(--ink-2))" }}>
                    {b.count} {b.count === 1 ? "find" : "finds"}
                    {b.avgPrice != null && <> · ${b.avgPrice}</>}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

      </div>
    </MobileFrame>
  );
}

// ─── Top Collectors strip (real data) ──────────────────────────────────────
// Lives at the bottom of the file so the main component stays readable.
function TopCollectorsStrip() {
  const nav = useNavigate();
  const { collectors, loading } = useTopCollectors(8, 7);

  return (
    <>
      <div style={{ padding: "0 20px 6px", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 22, color: "hsl(var(--ink))", letterSpacing: "-0.02em" }}>
          Top Collectors
        </div>
        <button onClick={() => nav("/leaders")} className="caps" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline", color: "hsl(var(--ink-2))" }}>
          Full board →
        </button>
      </div>
      {loading && (
        <div style={{ padding: "0 20px 22px" }}>
          <div style={{ border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))", padding: "14px", fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))", textAlign: "center" }}>
            tallying the board…
          </div>
        </div>
      )}
      {!loading && collectors.length === 0 && (
        <div style={{ padding: "0 20px 22px" }}>
          <div style={{ border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))", padding: "14px", fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))", textAlign: "center" }}>
            No posted finds in the last 7 days yet.
          </div>
        </div>
      )}
      {!loading && collectors.length > 0 && (
        <div
          className="no-scrollbar"
          style={{
            display: "flex", gap: 10, overflowX: "auto", padding: "0 20px 22px",
            scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch",
          }}
        >
          {collectors.map((c, i) => (
            <button
              key={c.user_id}
              onClick={() => nav(`/profile/${encodeURIComponent("@" + c.handle)}`)}
              style={{
                flex: "0 0 96px", scrollSnapAlign: "start",
                border: "none", background: "transparent",
                padding: "4px 2px", cursor: "pointer", textAlign: "center",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                position: "relative",
              }}
            >
              <div style={{ position: "relative" }}>
                <div style={{
                  width: 72, height: 72, borderRadius: "50%",
                  border: "1.5px solid hsl(var(--ink))",
                  background: "hsl(var(--paper-2))",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 26,
                  color: "hsl(var(--ink))", textTransform: "uppercase",
                }}>
                  {c.handle.slice(0, 2)}
                </div>
                <span style={{
                  position: "absolute", top: -4, right: -4,
                  minWidth: 22, height: 22, padding: "0 6px",
                  borderRadius: 999,
                  background: i === 0 ? "hsl(var(--rust))" : "hsl(var(--ink))",
                  color: i === 0 ? "hsl(var(--rust-fg))" : "hsl(var(--ink-fg))",
                  border: "1.5px solid hsl(var(--cream))",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fontWeight: 700,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  lineHeight: 1,
                }}>{c.rank}</span>
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
                color: "hsl(var(--ink))", lineHeight: 1.1,
                maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                @{c.handle}
              </div>
              <div className="caps" style={{ color: "hsl(var(--rust))", fontSize: 9 }}>
                {c.score} {c.score === 1 ? "find" : "finds"}
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

